import { AlertColor } from '@mui/material/Alert'
import axios, { AxiosError } from 'axios'
import React, { createContext, Dispatch, SetStateAction } from 'react'
import { v4 as uuidv4 } from 'uuid'
import Toast from '../components/Toast'
import UploadToast from '../components/UploadToast'

const ERROR_DURABILITY_MS = 10000

export interface Upload {
  id: string
  progress: number
}

export class StackContext {
  private _toasts: Array<JSX.Element>
  private _uploads: Array<Upload>
  private _setToasts: Dispatch<SetStateAction<Array<JSX.Element>>>
  private _setUploads: Dispatch<SetStateAction<Array<Upload>>>

  constructor(
    toasts: Array<JSX.Element>,
    uploads: Array<Upload>,
    setToasts: Dispatch<SetStateAction<Array<JSX.Element>>>,
    setUploads: Dispatch<SetStateAction<Array<Upload>>>,
  ) {
    this._setToasts = setToasts
    this._toasts = toasts
    this._uploads = uploads
    this._setUploads = setUploads
  }

  addError(error: Error) {
    if (axios.isAxiosError(error)) {
      this.addToast(
        (error as AxiosError).response?.data?.message || error.message || 'Unknown error.',
        ERROR_DURABILITY_MS,
      )
      return
    }

    this.addToast(error.message || 'Unknown error.', ERROR_DURABILITY_MS)
  }

  addToast(message: string, autoHideDuration: number | undefined, severity: AlertColor = 'error'): void {
    if (severity === 'error' && !message) {
      message = 'Unknown error.'
    }

    const id = uuidv4()

    this._setToasts([
      <Toast toastId={id} key={id} severity={severity} message={message} autoHideDuration={autoHideDuration} />,
      ...this._toasts,
    ])
  }

  addUploadToast(message: string): string {
    const id = uuidv4()

    const upload: Upload = { id, progress: 0 }
    this._setUploads([...this._uploads, upload])
    this._setToasts([<UploadToast toastId={id} key={id} message={message} />, ...this._toasts])

    return id
  }

  getToasts(): Array<JSX.Element> {
    return this._toasts
  }

  getUploads(): Array<Upload> {
    return this._uploads
  }

  setToasts(value: SetStateAction<Array<JSX.Element>>): void {
    this._setToasts(value)
  }

  setUploads(value: SetStateAction<Array<Upload>>): void {
    this._setUploads(value)
  }
}

export default createContext<StackContext>(
  new StackContext(
    [],
    [],
    () => undefined,
    () => undefined,
  ),
)
